import { useEffect, useState } from "react";
import { isRefused } from "../../services/apiClient";
import { honeyStockApi, poolApi } from "../../services/businessApi";
import { useToast } from "../../context/ToastContext";
import { ListBound } from "../../components/common/ListBound";
import { Card } from "../../components/common/Card";
import { Button } from "../../components/common/Button";
import { Badge } from "../../components/common/Badge";
import { DataTable } from "../../components/common/DataTable";
import { Modal } from "../../components/common/Modal";
import { Field } from "../../components/common/Field";
import { formatDateOnly } from "../../services/hiveActions";
import { useFeatureTheme } from "../../context/FeatureThemeContext";
import { GlassInventoryPanel } from "../../components/business/GlassInventoryPanel";
import { VarietalBreakdown } from "../../components/business/VarietalBreakdown";
import { BottleSizeManager } from "../../components/business/BottleSizeManager";
import { ActionMenu } from "../../components/common/ActionMenu";
import { VarietalPicker } from "../../components/common/VarietalPicker";
// One row per size, the varietal split only where there is one --
// mobile's judgement, already ported here and never called (#439).
import { shelfRows } from "../../services/bottledStock";
import { useFlow } from "../../hooks/useFlow";
import { FLOWS } from "../../services/flows";

/**
 * The honey pool: bulk honey waiting to be bottled (#191).
 *
 * The pool is a ledger server-side, so every write returns the recomputed
 * balance and this page uses THAT rather than adjusting a local number.
 * Someone correcting the pool on another device is invisible to arithmetic
 * done here.
 */

const TYPE_TONE = {
  Harvest: "success",
  Bottling: "info",
  Correction: "warning",
  Sale: "neutral",
  Loss: "danger",
};

/** "12 x 1 lb" reads better than "-12.000" for the thing you actually did. */
function describeMovement(m) {
  if (m.movement_type === "Bottling") {
    return `${m.bottle_count} x ${m.bottle_size_label || "bottles"}`;
  }
  const lbs = Math.abs(Number(m.pounds_delta)).toFixed(2).replace(/\.00$/, "");
  if (m.movement_type === "Correction") {
    // The sign is the whole meaning of a correction, so it is spelled out
    // rather than left as a minus sign the eye can skip.
    return Number(m.pounds_delta) < 0 ? `Down ${lbs} lb` : `Up ${lbs} lb`;
  }
  return `${lbs} lb`;
}

export function HoneyPoolPage() {
  const { notify } = useToast();
  // HoneyBusiness gates the CHOOSING and the breakdown, never the writing
  // (#225). A Harvester account still records a varietal on every row -- it
  // just lands on "General" -- so a later upgrade finds real history rather
  // than a season of blanks.
  const { hasFeature, canChangeLists } = useFeatureTheme();
  const showVarietals = hasFeature("HoneyBusiness");
  /*
   * The pool itself is HoneyPool (Free); bottling and everything downstream
   * of a jar is HoneyChain (Harvester) -- #247.
   *
   * A Free account reaches this page deliberately. It must show the pool,
   * the ledger, and both directions of movement, while offering nothing it
   * would be refused for pressing.
   */
  const canBottle = hasFeature("HoneyChain");
  const [pool, setPool] = useState(null);
  const [sizes, setSizes] = useState([]);
  const [movements, setMovements] = useState(null);
  // The pool's log is capped too (#372), and it is what somebody checks
  // when a number looks wrong -- so it has to say where it stops.
  const [movementsCappedAt, setMovementsCappedAt] = useState(null);
  // The error itself, or null -- a refusal is not a failure. See load().
  const [loadFailed, setLoadFailed] = useState(null);

  const [bottleOpen, setBottleOpen] = useState(false);
  /* #379. Bottling is the flow on this page with arithmetic in it --
     "is this slower than it looks" is a real question about it, and
     an abandoned bottling run is somebody who could not work out
     what to enter. */
  const bottleFlow = useFlow(FLOWS.BOTTLING, bottleOpen);
  // The server's needs_confirmation payload for a bottling beyond stock
  // (#237), or null.
  const [shortfall, setShortfall] = useState(null);
  const [exceeded, setExceeded] = useState([]);
  const [bottleForm, setBottleForm] = useState({ bottle_size_id: "", bottle_count: "", reason: "", varietal_id: "" });
  const [removeOpen, setRemoveOpen] = useState(false);
  // No default disposition: the two are not interchangeable, and the server
  // refuses anything that is not one of them rather than guessing.
  const [removeForm, setRemoveForm] = useState({ pounds: "", disposition: "", reason: "", varietal_id: "" });
  const [unbottleOpen, setUnbottleOpen] = useState(false);
  const [unbottleForm, setUnbottleForm] = useState({ bottle_size_id: "", bottle_count: "", reason: "", varietal_id: "" });
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ pounds: "", reason: "", varietal_id: "" });
  const [correctOpen, setCorrectOpen] = useState(false);
  const [correctForm, setCorrectForm] = useState({ pounds: "", reason: "", varietal_id: "" });
  // Balances per varietal, so the correction dialog can quote the SAME
  // number it is about to compare against. Quoting the whole tank while
  // correcting one varietal is what produced "-14" on 25 Aug.
  const [byVarietal, setByVarietal] = useState(null);
  // The usual price for each size and varietal (V081). A DEFAULT only --
  // every transaction records its own unit_price when it happens, so
  // changing one of these never touches what a past sale went out at.
  const [prices, setPrices] = useState({});
  const [editingPrice, setEditingPrice] = useState(null);
  // Opened from the shelf's own header, so the size list is reachable
  // from where somebody notices it needs tidying (#417's rule).
  const [managingSizes, setManagingSizes] = useState(false);
  // Add lives in the CARD HEADER and the dialog lives in the manager,
  // so the flag has to cross between them (#410).
  const [addingSize, setAddingSize] = useState(false);
  const [priceDraft, setPriceDraft] = useState("");

  async function load() {
    try {
      /*
       * Sizes are fetched only where bottling exists (#247).
       *
       * listSizes is HoneyChain. Inside the old Promise.all, a Free
       * account's single 403 on that one call rejected the whole thing and
       * the page rendered "this is part of the Harvester plan" over a pool
       * the account is entitled to -- the #242 failure, reintroduced by
       * opening the page to a tier that cannot call every endpoint on it.
       */
      const [p, m] = await Promise.all([poolApi.get(), poolApi.movements()]);
      setPool(p);
      setMovements(m.movements || []);
      setMovementsCappedAt(m.capped_at ?? null);
      if (canBottle) {
        const s = await poolApi.listSizes();
        setSizes(s.bottle_sizes || []);
      } else {
        setSizes([]);
      }
      setLoadFailed(null);
    } catch (err) {
      // Distinct from "the pool is empty" -- see the render below. Showing
      // 0 lb to someone with a full tank is worse than showing nothing.
      //
      // The ERROR is kept, not a boolean: a refusal (this plan does not
      // include the honey chain) and a dead connection need different words
      // and different buttons (#245).
      setLoadFailed(err);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [canBottle]);
  // The remembered prices (V081). Separate from load() because it is
  // best-effort: a failure here leaves the forms asking for a price,
  // which is exactly what they did before, and must never fail the page.
  useEffect(() => { loadPrices(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [showVarietals]);

  // Its own effect, outside load()'s try. The review list is a season-end
  // nicety and must not be able to make the pool balance itself look
  // unloadable -- which is exactly what happened when it lived inside.
  useEffect(() => {
    let cancelled = false;
    if (!canBottle) return;
    poolApi
      .listExceeded()
      .then((r) => !cancelled && setExceeded(r.exceeded || []))
      .catch(() => !cancelled && setExceeded([]));
    return () => { cancelled = true; };
  }, [canBottle]);


  async function submitBottle(acknowledgeShortfall = false) {
    const count = Number(bottleForm.bottle_count);
    /* `count > 0`, NOT `!count`. A falsy check catches 0 and NaN and lets
       -5 through, because -5 is truthy -- so a negative made the round
       trip to be refused by the schema's gt=0, and came back as a message
       nobody could act on. Mobile had the same check, and there it is
       worse: bottling queues offline, so the negative waits and fails on
       a conflict dashboard hours later. */
    if (!bottleForm.bottle_size_id || !(count > 0)) return notify("Pick a size and how many bottles", "warning");
    try {
      const r = await poolApi.bottle(
        Number(bottleForm.bottle_size_id), count, bottleForm.reason || undefined,
        // Empty on a tier with no picker; the server fills in "General".
        bottleForm.varietal_id ? Number(bottleForm.varietal_id) : undefined,
        acknowledgeShortfall,
      );

      // More honey than the pool records (#237). A 200 carrying a question,
      // not an error -- nothing has been written, and the numbers to ask
      // with came back with it. Never re-posted automatically: that would
      // authorise the over-draw on the user's behalf.
      if (r.needs_confirmation === "shortfall") {
        setShortfall(r);
        return;
      }

      setShortfall(null);
      /*
       * What happened to the JARS, when this account counts them.
       *
       * `glass` is absent when the size is not tracked, and that absence
       * carries meaning: the server keeps "not tracking" distinct from
       * "zero jars", because a confident 0 that really means "we don't
       * know" sends somebody out to buy glass they already have. So this
       * says nothing rather than reporting a count of none.
       */
      const glass = r.glass
        ? r.glass.short
          // The clamp is the moment worth mentioning: the shelf and the
          // app had already drifted, and this is where that surfaces.
          ? ` · only ${r.glass.used} clean jars were counted, so that count is now 0`
          : ` · ${r.glass.used} jars used, ${r.glass.clean_left} clean left`
        : "";
      // The server writes the sentence when a run went beyond stock, so the
      // negative balance is explained rather than discovered later.
      notify(
        `${r.message || `Bottled ${r.bottles} — ${r.pounds_used} lb used, ${r.pool_pounds} lb left`}${glass}`,
        "success",
      );
      /* AFTER the shortfall question, not before it: a run the keeper
         has not yet confirmed is still in progress, and counting it
         earlier would record a completion for a dialog they then
         cancelled. */
      bottleFlow.finished();
      setBottleOpen(false);
      setBottleForm({ bottle_size_id: "", bottle_count: "", reason: "" });
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not record bottling", "danger");
    }
  }

  async function submitUnbottle() {
    const count = Number(unbottleForm.bottle_count);
    // Same falsy-check bug as bottling: -5 is truthy.
    if (!unbottleForm.bottle_size_id || !(count > 0)) return notify("Pick a size and how many jars", "warning");
    try {
      const r = await poolApi.unbottle(
        Number(unbottleForm.bottle_size_id), count, unbottleForm.reason || undefined,
        /* "" is unchosen and "none" is the explicit unspecified varietal.
           Both mean "do not send one"; only a real id travels. */
        unbottleForm.varietal_id && unbottleForm.varietal_id !== "none"
          ? Number(unbottleForm.varietal_id)
          : undefined,
      );
      // The POOL TOTAL, not just the amount recovered. The whole reason to
      // unbottle is to refill in another size, so the next question is "can
      // I fill that order now" -- which the delta cannot answer.
      notify(
        `Emptied ${r.bottles_emptied} — pool is now ${r.pool_pounds} lb, ${r.bottles_left} jars left`,
        "success",
      );
      setUnbottleOpen(false);
      setUnbottleForm({ bottle_size_id: "", bottle_count: "", reason: "", varietal_id: "" });
      load();
    } catch (err) {
      // The server names how many are actually on hand when there are too few.
      notify(err.apiMessage || "Could not empty those jars", "danger");
    }
  }

  async function submitAdd() {
    const pounds = Number(addForm.pounds);
    /* This is the one that was asked about: "what happens if a user
       enters a negative number in the add honey screen?" On web, -40 got
       past this check and was refused by the server. Mobile's copy of
       this form already tested `value <= 0`; the desk's did not, which is
       the one-path shape -- the same question answered on one platform
       and not the other. */
    if (!(pounds > 0)) return notify("Enter how many pounds went in", "warning");
    try {
      const r = await poolApi.add(
        pounds, addForm.reason || undefined,
        addForm.varietal_id ? Number(addForm.varietal_id) : undefined,
      );
      notify(`Pool is now ${r.pool_pounds} lb`, "success");
      setAddOpen(false);
      setAddForm({ pounds: "", reason: "" });
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not add to the pool", "danger");
    }
  }

  async function submitRemove() {
    const pounds = Number(removeForm.pounds);
    // Negative pounds would be honey arriving through the form for
    // removing it. The endpoint negates the sign it is given (V037), so
    // this is the only place it can be asked about honestly.
    if (!(pounds > 0)) return notify("Enter how many pounds left the pool", "warning");
    if (!removeForm.disposition) return notify("Say whether it was sold or lost", "warning");
    try {
      const r = await poolApi.remove(
        pounds,
        removeForm.disposition,
        removeForm.reason || undefined,
        removeForm.varietal_id ? Number(removeForm.varietal_id) : undefined,
      );
      notify(`Removed ${pounds} lb — ${r.pool_pounds} lb left`, "success");
      setRemoveOpen(false);
      setRemoveForm({ pounds: "", disposition: "", reason: "", varietal_id: "" });
      load();
    } catch (err) {
      // Includes the over-draw refusal, which names both numbers and points
      // at Correct Pool. The server's sentence is the one shown, so web and
      // mobile cannot drift into two different explanations.
      notify(err.apiMessage || "Could not remove from the pool", "danger");
    }
  }

  const priceKey = (sizeId, varietalId) => `${sizeId}-${varietalId}`;

  async function loadPrices() {
    if (!showVarietals) return;
    try {
      const r = await honeyStockApi.bottlePrices();
      const map = {};
      for (const row of r.prices || []) {
        map[priceKey(row.bottle_size_id, row.varietal_id)] = Number(row.unit_price);
      }
      setPrices(map);
    } catch {
      // Best-effort. Without them the forms simply ask for a price, which
      // is what they did before -- never a reason to fail the page.
      setPrices({});
    }
  }

  async function savePrice(row) {
    const value = Number(priceDraft);
    if (priceDraft === "" || Number.isNaN(value) || value < 0) {
      notify("Enter a price, or 0 if you give it away", "warning");
      return;
    }
    try {
      await honeyStockApi.setBottlePrice({
        bottle_size_id: row.bottle_size_id,
        varietal_id: row.varietal_id,
        unit_price: value,
      });
      setPrices((p) => ({ ...p, [priceKey(row.bottle_size_id, row.varietal_id)]: value }));
      setEditingPrice(null);
      notify("Saved. It'll be filled in next time — you can still change it per sale.", "success");
    } catch (err) {
      notify(err.apiMessage || "Couldn't save that price", "danger");
    }
  }

  async function openCorrect() {
    setCorrectOpen(true);
    if (!showVarietals) return;
    try {
      const r = await poolApi.byVarietal();
      setByVarietal(r.by_varietal || []);
    } catch {
      // Best effort. Without it the dialog quotes no figure at all,
      // which is better than quoting the wrong one.
      setByVarietal(null);
    }
  }

  /** What the app currently thinks the SELECTED varietal holds. */
  function currentForSelection() {
    if (!showVarietals || byVarietal === null) return null;
    const chosen = correctForm.varietal_id;
    const row = chosen
      ? byVarietal.find((v) => String(v.varietal_id) === String(chosen))
      : byVarietal.find((v) => v.varietal_id == null || v.is_default);
    // A varietal with no movements yet holds nothing, and saying so is
    // more useful than saying nothing.
    return row ? Number(row.pounds) : 0;
  }

  async function submitCorrect() {
    if (correctForm.pounds === "") return notify("Enter what the tank actually reads", "warning");
    try {
      const r = await poolApi.correct(
        Number(correctForm.pounds),
        correctForm.reason || undefined,
        correctForm.varietal_id || undefined,
      );
      notify(
        r.unchanged
          ? "That matches what the app had — nothing to change"
          : `Pool corrected by ${r.adjusted_by} lb, now ${r.pool_pounds} lb`,
        "success",
      );
      setCorrectOpen(false);
      setCorrectForm({ pounds: "", reason: "", varietal_id: "" });
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not correct the pool", "danger");
    }
  }



  if (loadFailed) {
    return (
      <div>
        <h1>Honey Pool</h1>
        <Card>
          {/* Retrying a 403 produces the same 403 forever. Say which plan
              carries this and stop offering a button that cannot work. */}
          <p>
            {isRefused(loadFailed)
              ? loadFailed.apiMessage
                || "This part of the honey chain — bottling, jars and vendors — is part of the Harvester plan."
              : "Couldn't load the honey pool. Check your connection and try again."}
          </p>
          {isRefused(loadFailed) ? null : <Button onClick={load}>Try again</Button>}
        </Card>
      </div>
    );
  }

  const poolPounds = pool ? Number(pool.pool_pounds) : null;
  const noSizes = sizes.length === 0;
  // Only sizes with jars actually on the shelf can be emptied. The server
  // already nets bottling against unbottling and drops sizes that reach
  // zero, so this needs no filtering of its own -- but guard the shape in
  // case the pool has not loaded yet.
  const bottledSizes = (pool?.bottled_by_size || []).filter((b) => Number(b.bottles) > 0);
  const hasBottled = bottledSizes.length > 0;

  /*
   * UNBOTTLING ASKS WHICH HONEY FIRST (#384, option 2).
   *
   * Bottling already did. Unbottling asked for the size, then the amount,
   * then finally what the honey actually was -- and its varietal options
   * were FILTERED BY the size, which is why it sat underneath: "asking
   * first meant an empty dropdown until you went back and answered the
   * question underneath it."
   *
   * So the dependency is inverted rather than the fields merely swapped.
   * Varietals come from everything on the shelf, summed across sizes;
   * sizes are then narrowed to the ones holding THAT honey. Same data,
   * asked in the order somebody actually decides: which jars, then how
   * many.
   *
   * "" is UNCHOSEN and "none" is the explicit unspecified varietal. They
   * were the same value before, which worked only because the field could
   * not be reached before a size was picked.
   */
  const UNSPECIFIED = "none";
  const unbottleVarietals = Object.values(
    (pool?.bottled_by_varietal || []).reduce((acc, b) => {
      const key = b.varietal_id == null ? UNSPECIFIED : String(b.varietal_id);
      acc[key] ||= { key, varietal_name: b.varietal_name, bottles: 0 };
      acc[key].bottles += Number(b.bottles) || 0;
      return acc;
    }, {}),
  ).filter((v) => v.bottles > 0);

  /* The sizes holding the chosen honey. With nothing chosen yet, every
     size that has jars -- so an account with varietals switched off sees
     exactly what it saw before. */
  const unbottleSizes = !unbottleForm.varietal_id
    ? bottledSizes
    : (pool?.bottled_by_varietal || [])
      .filter((b) => (b.varietal_id == null ? UNSPECIFIED : String(b.varietal_id)) === unbottleForm.varietal_id)
      .filter((b) => Number(b.bottles) > 0)
      .map((b) => ({ bottle_size_id: b.bottle_size_id, label: b.label, bottles: b.bottles }));

  return (
    <div>
      <h1>Honey Pool</h1>

      <Card title="In the pool">
        {/* The number people came for, at a size they can read across a
            workshop. Everything else on the page is in service of it. */}
        <div style={{ fontSize: "var(--font-size-xxl, 2.5rem)", fontWeight: 700 }}>
          {poolPounds === null ? "—" : `${poolPounds.toFixed(2).replace(/\.00$/, "")} lb`}
        </div>
        <p style={{ color: "var(--color-text-muted)" }}>Bulk honey not yet bottled.</p>
        {/* Five equally-sized buttons became two and a menu (#327).
            docs/UI_ACTIONS.md names this page as the one the four-visible
            rule would hurt, and it is the same row that made the point:
            a user scanning it was doing triage the screen should have
            done.

            What stays out is what people came to do. Bottling is the
            job. Add Honey is the other everyday direction -- though less
            so since V091, which means a honey harvest now credits the
            pool by itself and this button is for honey that never had a
            harvest row.

            What goes in the menu is the three that are rarer AND want a
            moment's thought: undoing a bottling run, taking honey out
            for a reason that is not bottling, and reconciling against
            the tank. Order is left-to-right [secondary] [primary], rule
            8, so the primary sits where the eye finishes. */}
        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
          <ActionMenu
            label="More"
            items={[
              // Disabled reasons travel with the item. A menu that hides
              // WHY something cannot be pressed is worse than a greyed
              // button, because the user has to open it to find out.
              canBottle && {
                label: hasBottled ? "Unbottle" : "Unbottle (nothing bottled)",
                onSelect: () => setUnbottleOpen(true),
                disabled: !hasBottled,
              },
              {
                // The other direction (#247). Without it a pool only ever
                // grows, and a Free account -- which cannot bottle --
                // would watch the number drift away from the tank until
                // it stopped meaning anything.
                label: poolPounds ? "Remove Honey" : "Remove Honey (pool is empty)",
                onSelect: () => setRemoveOpen(true),
                disabled: !poolPounds,
              },
              {
                // A correction is a first-class action, not a hidden edit
                // -- bottling is inexact and the app WILL drift from the
                // tank. Being in a menu does not demote it; it is here
                // because it is occasional, not because it is minor.
                label: "Correct Pool",
                onSelect: openCorrect,
              },
            ]}
          />
          <Button variant={canBottle ? "secondary" : "primary"} onClick={() => setAddOpen(true)}>Add Honey</Button>
          {canBottle && <Button onClick={() => setBottleOpen(true)} disabled={noSizes}>Bottle Honey</Button>}
        </div>
        {canBottle && noSizes ? (
          <p style={{ color: "var(--color-text-muted)" }}>
            Add a bottle size below before you can bottle.
          </p>
        ) : null}
      </Card>

      {/* Reported 28 Aug: these were touching. */}
      <div style={{ height: "var(--space-5)" }} />

      {/* Both Business-tier. Each renders nothing when it has nothing
          truthful to say -- one varietal is not a breakdown, and an account
          that does not count glass sees an invitation, not a grid of
          zeros. */}
      {/* The breakdown OWNS the manage panel now (#410). It used to sit
          permanently below as a second card, which put the varietal list
          on the page twice -- once as the numbers people came for, once
          as the same names with buttons. Curating is behind Manage on
          this card's header, still under the same Business gate:
          separating honey into streams is the Business promise, and
          tidying the list is part of that act rather than a separate
          feature (#358). */}
      {showVarietals && (
        <VarietalBreakdown poolPounds={pool?.pool_pounds} onChanged={load} canChangeLists={canChangeLists} />
      )}
      {/* The season-end review (#237). Every row is a missing extraction or
          a mis-estimate, which is why the shortfall is stored at the time
          rather than derived from a ledger a later correction can move. */}
      {exceeded.length > 0 && (
        <Card title="Bottled beyond stock" className="section-spacing">
          <p style={{ marginTop: 0, color: "var(--color-text-muted)" }}>
            These runs recorded more honey than the pool had at the time. Each one is
            either an extraction that never got logged or a count worth checking.
          </p>
          <DataTable
            rows={exceeded}
            rowKey="movement_id"
            columns={[
              { key: "created_date", header: "When", render: (r) => formatDateOnly(r.created_date) },
              { key: "bottle_size_label", header: "Bottled", render: (r) => `${r.bottle_count} x ${r.bottle_size_label}` },
              { key: "varietal_name", header: "Varietal" },
              { key: "exceeded_by_pounds", header: "Over by", render: (r) => `${Number(r.exceeded_by_pounds)} lb` },
            ]}
            emptyTitle="Nothing over stock"
            emptyDescription="Bottling that exceeded the pool would show up here."
          />
        </Card>
      )}

      {/* Which honey is in the jars (#225 follow-up).
          Bottling has always recorded the varietal; the shelf figure just
          grouped it away, so "after bottling the varietal is lost" was true
          of the screen and not of the data. Business only, like every other
          varietal view -- a Harvester account has one pool and one answer. */}
      {/*
        * ONE CARD FOR "WHAT IS ON THE SHELF" (#439).
        *
        * This was two tables answering overlapping questions -- "Bottled,
        * by varietal" (varietal / size / jars / price) and "Bottled so
        * far" (size / bottles / pounds) -- plus a bottle-sizes card below
        * them as a third place the same sizes appeared.
        *
        * Asked for after noticing the same stock is presented differently
        * on every screen that shows it: "Currently I think the best view
        * is the mobile Bottled on the shelf version."
        *
        * It is also the failure this repo has shipped twice. #395: one
        * list asked both "what do I own" and "what did I buy". #402:
        * "there are 3 buttons and 2 do the same thing." Several cards
        * answering overlapping questions is how somebody reads the wrong
        * one -- and these are the numbers a keeper sells from.
        *
        * shelfRows() ALREADY EXISTED HERE, identical to mobile's, with no
        * caller anywhere in web. The judgement was ported and never wired
        * up -- the orphan-export shape scripts/orphan_exports.py hunts,
        * found by reaching for it.
        */}
      {canBottle && (
        <Card
          title="Bottled, by varietal"
          className="section-spacing"
          /* MANAGE SITS WHERE THE QUESTION IS ASKED, which is #417's rule
             for "Manage types" moving out of the Add form. Somebody
             tidying the size list noticed it HERE, reading the counts. */
          /* ADD WHERE MANAGE WAS (#410), which is what "By varietal" does
             two cards up: the same corner, and only one of them is useful
             at a time. This read "Done" while managing -- a second way out
             beside the manager's own, for a panel that was also a second
             card with a second Add. */
          actions={canChangeLists === false ? null : managingSizes
            ? <Button size="sm" variant="secondary" onClick={() => setAddingSize(true)}>Add</Button>
            : <Button size="sm" variant="secondary" onClick={() => setManagingSizes(true)}>Manage sizes</Button>}
        >
          {/* MANAGING REPLACES THE SHELF, it does not sit under it (#410).
              Two lists of the same jar sizes, one above the other, is how
              somebody edits the wrong one -- and merging exactly that
              duplication out of this page is what #439 was. "By varietal"
              two cards up already works this way.

              BottleSizeManager rather than BottleSizesCard because a card
              cannot be nested in a card; the card version is still the
              standalone face of the same list. */}
          {managingSizes ? (
            <BottleSizeManager
              sizes={sizes}
              adding={addingSize}
              onAddingClose={() => setAddingSize(false)}
              onChanged={load}
              onClose={() => { setManagingSizes(false); setAddingSize(false); }}
            />
          ) : (
            <DataTable
              /* FALLS BACK TO bottled_by_size, and that is not defensive
                 tidiness -- the two tables this replaces read DIFFERENT
                 fields, and dropping one silently emptied the shelf for
                 anybody whose API has not shipped the varietal split yet
                 (a cached client, or a Harvester account that never has
                 one). A test written for exactly that case caught it.

                 The fallback lives in shelfRows() rather than here,
                 because mobile had the same hole and hid it better --
                 its card is gated on the rows existing, so the whole
                 shelf silently vanished instead of emptying. */
              rows={shelfRows(pool?.bottled_by_varietal, sizes, pool?.bottled_by_size)}
              rowKey="bottle_size_id"
              columns={[
                { key: "label", header: "Size" },
                {
                  key: "total",
                  header: "Jars",
                  render: (r) => (
                    <span>
                      <strong>{r.total}</strong>
                      {/* THE SPLIT ONLY WHERE THERE IS ONE TO SHOW, which is
                          mobile's judgement and the reason that view was
                          picked as the reference: repeating the size total
                          under it as a single varietal line is the same
                          number twice, and reads as a bug. */}
                      {/* THE NAME EVEN WHEN THERE IS ONLY ONE (#451), and
                          the COUNT only when there are several.

                          The original rule refused to repeat the size
                          total beneath itself as a single varietal line,
                          because the same number twice reads as a bug.
                          That argument was about the NUMBER and it still
                          holds; it was never an argument about the name.

                          Reported: "if you have helpers they won't know at
                          a glance" which honey is in the jars. The owner
                          remembers what is in the 2 lb jars. The person who
                          came to help pack a vendor order does not, and
                          this card is what they read while doing it --
                          which is the same helper case #442 and #415 exist
                          for. */}
                      {showVarietals && r.varietals.length > 0 ? (
                        <span style={{
                          display: "block",
                          color: "var(--color-text-muted)",
                          fontSize: "var(--font-size-sm)",
                        }}>
                          {r.varietals.length > 1
                            ? r.varietals.map((v) => `${v.name} ${v.bottles}`).join(" · ")
                            /* varietal_id, not the name. shelfRows labels a
                               NULL varietal "Unspecified", and printing that
                               under a size tells a helper nothing they did
                               not already see -- honey nobody separated has
                               no name worth showing. */
                            : r.varietals[0].varietal_id != null
                              ? r.varietals[0].name
                              : null}
                        </span>
                      ) : null}
                    </span>
                  ),
                },
                {
                  /* The usual price, edited where the stock is read. It
                     fills in Send Honey, Accept Return and Sold, and stays
                     editable there -- so a one-off discount changes that
                     sale and nothing else.
                     Per SIZE here rather than per size+varietal: a row is a
                     size now. A size holding several varietals at different
                     prices keeps them; this edits the one that applies when
                     the size holds a single varietal, which is the case the
                     price was being set for. */
                  key: "usual_price",
                  header: "Usual price",
                  render: (r) => {
                    const only = r.varietals.length === 1 ? r.varietals[0] : null;
                    if (!only) {
                      return (
                        <span style={{ color: "var(--color-text-muted)" }}>
                          {/* Honest rather than blank: several varietals in
                              one size have several prices, and showing one
                              of them here would be a number somebody acts
                              on. */}
                          Several
                        </span>
                      );
                    }
                    const key = `${r.bottle_size_id}-${only.varietal_id}`;
                    const known = prices[key];
                    if (editingPrice === key) {
                      return (
                        <span style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}
                          onClick={(e) => e.stopPropagation()}>
                          <input id="honey-pool-page-price-draft" name="price-draft" type="number" step="0.01" min="0" autoFocus
                            value={priceDraft} className="touch-target"
                            style={{ width: "5.5rem" }}
                            onChange={(e) => setPriceDraft(e.target.value)} />
                          <Button size="sm" onClick={() => savePrice({
                            bottle_size_id: r.bottle_size_id, varietal_id: only.varietal_id,
                          })}>Save</Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingPrice(null)}>Cancel</Button>
                        </span>
                      );
                    }
                    return (
                      <span onClick={(e) => e.stopPropagation()}>
                        <Button size="sm" variant="ghost"
                          onClick={() => { setEditingPrice(key); setPriceDraft(known == null ? "" : String(known)); }}>
                          {/* Nothing remembered reads as "Set", not as $0.00
                              -- a price of zero is a real answer somebody
                              may have chosen. */}
                          {known == null ? "Set" : `$${known.toFixed(2)}`}
                        </Button>
                      </span>
                    );
                  },
                },
              ]}
              emptyTitle="Nothing bottled yet"
              emptyDescription="Bottling from the pool will show up here."
            />
          )}
        </Card>
      )}

      {/* GLASS BELOW THE SHELF, not above it. Asked for in that order,
          and it reads that way: what is IN the jars comes before what the
          jars themselves are doing. */}
      {showVarietals && <GlassInventoryPanel sizes={sizes} />}

      <Card title="History" className="section-spacing">
        <DataTable
          rows={movements}
          rowKey="movement_id"
          columns={[
            { key: "created_date", header: "When", render: (r) => formatDateOnly(r.created_date) },
            { key: "movement_type", header: "What", render: (r) => <Badge tone={TYPE_TONE[r.movement_type] || "neutral"}>{r.movement_type}</Badge> },
            { key: "detail", header: "Detail", render: describeMovement },
            { key: "reason", header: "Note", render: (r) => r.reason || "" },
          ]}
          emptyTitle="Nothing recorded yet"
          emptyDescription="Everything in and out of the pool appears here."
        />
        {/* No filters here, so no advice to give -- just the honest
            statement that the log goes back this far (#372). */}
        <ListBound cappedAt={movementsCappedAt} noun="movements" />
      </Card>

      <Modal
        open={bottleOpen}
        onClose={() => { setBottleOpen(false); setShortfall(null); }}
        title="Bottle Honey"
        footer={
          shortfall ? (
            // Two deliberate presses, and the confirm is not the one the
            // eye lands on first. Consent is never inferred from a retry.
            <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
              <Button variant="secondary" onClick={() => { setShortfall(null); setBottleOpen(false); setAddOpen(true); }}>
                Add the extraction first
              </Button>
              <Button onClick={() => submitBottle(true)}>Bottle anyway</Button>
            </div>
          ) : (
            <Button onClick={() => submitBottle()}>Record Bottling</Button>
          )
        }
      >
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          {/* More honey than the pool records (#237). Not a refusal: the
              jars are usually already filled, and it is the pool balance
              that was wrong. The wording comes from the server so web and
              mobile cannot drift into two different sentences. */}
          {shortfall && (
            <div role="alert" style={{ display: "grid", gap: "var(--space-2)" }}>
              <strong>{shortfall.message}</strong>
              <span style={{ color: "var(--color-text-muted)" }}>
                {shortfall.bottle_count} x {shortfall.bottle_size_label} needs {shortfall.pounds_needed} lb;
                the pool has {shortfall.pounds_available} lb. Bottling anyway leaves it at{" "}
                {shortfall.resulting_pool_pounds} lb until the missing honey is added, and flags the
                run for review.
              </span>
            </div>
          )}
          {/* Only where it can be chosen. The row records a varietal
              regardless -- see the note at the top of this component. */}
          <VarietalPicker purpose="move" /* Bottling moves pool honey into jars. (#358) */
            enabled={showVarietals}
            value={bottleForm.varietal_id}
            onChange={(v) => setBottleForm((f) => ({ ...f, varietal_id: v }))}
            /* The server files an omitted varietal under General anyway,
               so showing it is a restatement of what happens rather than
               a guess (#384 follow-up). */
            defaultGeneral
          />
          <Field label="Bottle size">
            {(id, aria) => (
              <select id={id} {...aria} value={bottleForm.bottle_size_id}
                onChange={(e) => setBottleForm((f) => ({ ...f, bottle_size_id: e.target.value }))} className="touch-target">
                <option value="">Select a size...</option>
                {sizes.map((s) => <option key={s.bottle_size_id} value={s.bottle_size_id}>{s.label} ({Number(s.pounds)} lb)</option>)}
              </select>
            )}
          </Field>
          <Field label="How many bottles?" hint="You can bottle in batches — record each session separately.">
            {(id, aria) => (
              <input id={id} {...aria} type="number" min="1" value={bottleForm.bottle_count}
                onChange={(e) => setBottleForm((f) => ({ ...f, bottle_count: e.target.value }))} className="touch-target" />
            )}
          </Field>
          <Field label="Note (optional)">
            {(id, aria) => <input id={id} {...aria} value={bottleForm.reason}
              onChange={(e) => setBottleForm((f) => ({ ...f, reason: e.target.value }))} className="touch-target" />}
          </Field>
        </div>
      </Modal>

      <Modal open={unbottleOpen} onClose={() => setUnbottleOpen(false)} title="Empty Jars Back Into the Pool"
             footer={<Button onClick={submitUnbottle}>Record Unbottling</Button>}>
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          <p style={{ marginTop: 0, color: "var(--color-text-muted)" }}>
            For when jars need emptying to re-fill in another size. The honey goes back
            into the pool and those jars come off your shelf count.
          </p>
          {/* WHICH HONEY FIRST (#384). See unbottleVarietals above for why
              the dependency is inverted rather than the fields swapped. */}
          {showVarietals && unbottleVarietals.length > 0 && (
            <Field label="Which honey?" hint="Only what is actually on the shelf — a varietal with no jars cannot be emptied.">
              {(id, aria) => (
                <select id={id} {...aria} className="touch-target" value={unbottleForm.varietal_id}
                  onChange={(e) => setUnbottleForm((f) => (
                    // A different honey may not come in the size already
                    // chosen, and silently emptying jars of another
                    // varietal is the drift #359 fixed.
                    { ...f, varietal_id: e.target.value, bottle_size_id: "" }
                  ))}>
                  <option value="">Select a varietal...</option>
                  {unbottleVarietals.map((v) => (
                    <option key={v.key} value={v.key}>{v.varietal_name} ({v.bottles} on hand)</option>
                  ))}
                </select>
              )}
            </Field>
          )}
          <Field label="Bottle size">
            {(id, aria) => (
              <select id={id} {...aria} value={unbottleForm.bottle_size_id}
                onChange={(e) => setUnbottleForm((f) => ({ ...f, bottle_size_id: e.target.value }))} className="touch-target">
                <option value="">Select a size...</option>
                {/* Only sizes with jars OF THE CHOSEN HONEY, showing the
                    count -- offering a size with none is an invitation to
                    an error message. */}
                {unbottleSizes.map((b) => (
                  <option key={b.bottle_size_id} value={b.bottle_size_id}>{b.label} ({b.bottles} on hand)</option>
                ))}
              </select>
            )}
          </Field>
          <Field label="How many jars?">
            {(id, aria) => (
              <input id={id} {...aria} type="number" min="1" value={unbottleForm.bottle_count}
                onChange={(e) => setUnbottleForm((f) => ({ ...f, bottle_count: e.target.value }))} className="touch-target" />
            )}
          </Field>
          <Field label="Note (optional)" hint="e.g. re-filling as 2 lb for the Fenwick order">
            {(id, aria) => <input id={id} {...aria} value={unbottleForm.reason}
              onChange={(e) => setUnbottleForm((f) => ({ ...f, reason: e.target.value }))} className="touch-target" />}
          </Field>
        </div>
      </Modal>

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Add Honey to the Pool" footer={<Button onClick={submitAdd}>Add</Button>}>
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          {/* Only where it can be chosen. The row records a varietal
              regardless -- see the note at the top of this component. */}
          <VarietalPicker purpose="record" /* Adding honey it did not have before. (#358) */
            enabled={showVarietals}
            value={addForm.varietal_id}
            onChange={(v) => setAddForm((f) => ({ ...f, varietal_id: v }))}
            defaultGeneral
          />
          <Field label="Pounds" hint="Usually what came off an extraction.">
            {(id, aria) => <input id={id} {...aria} type="number" step="0.01" min="0" value={addForm.pounds}
              onChange={(e) => setAddForm((f) => ({ ...f, pounds: e.target.value }))} className="touch-target" />}
          </Field>
          <Field label="Note (optional)">
            {(id, aria) => <input id={id} {...aria} value={addForm.reason}
              onChange={(e) => setAddForm((f) => ({ ...f, reason: e.target.value }))} className="touch-target" />}
          </Field>
        </div>
      </Modal>

      {/* The other direction (#247). Deliberately mirrors Add Honey field
          for field -- same varietal picker, same note -- so the pair reads
          as one idea rather than two unrelated forms. */}
      <Modal open={removeOpen} onClose={() => setRemoveOpen(false)} title="Remove Honey from the Pool"
        footer={<Button onClick={submitRemove}>Remove</Button>}>
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
            For honey that left the pool without being bottled — sold in bulk, or
            spilled, spoiled or otherwise lost.
          </p>
          <VarietalPicker purpose="move" /* Removing honey it already holds. (#358) */
            enabled={showVarietals}
            value={removeForm.varietal_id}
            onChange={(v) => setRemoveForm((f) => ({ ...f, varietal_id: v }))}
            defaultGeneral
          />
          <Field label="Pounds">
            {(id, aria) => <input id={id} {...aria} type="number" step="0.01" min="0" value={removeForm.pounds}
              onChange={(e) => setRemoveForm((f) => ({ ...f, pounds: e.target.value }))} className="touch-target" />}
          </Field>
          {/* No preselected option. Sold honey left as product and lost
              honey did not; a year on that is the difference between a good
              season and a bad one, and the ledger is append-only. */}
          <Field label="What happened to it" required>
            {(id, aria) => (
              <select id={id} {...aria} className="touch-target" value={removeForm.disposition}
                onChange={(e) => setRemoveForm((f) => ({ ...f, disposition: e.target.value }))}>
                <option value="">Choose…</option>
                <option value="Sale">Sold — straight from the bucket, unbottled</option>
                <option value="Loss">Lost — spilled, spoiled, or given away</option>
              </select>
            )}
          </Field>
          <Field label="Note (optional)">
            {(id, aria) => <input id={id} {...aria} value={removeForm.reason}
              onChange={(e) => setRemoveForm((f) => ({ ...f, reason: e.target.value }))} className="touch-target" />}
          </Field>
        </div>
      </Modal>

      <Modal open={correctOpen} onClose={() => setCorrectOpen(false)} title="Correct the Pool" footer={<Button onClick={submitCorrect}>Save Correction</Button>}>
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          {/* Which honey the correction applies to (reported 25 Aug).
              The endpoint has always accepted this and the form never
              sent it, so every correction landed on the default -- and
              there was no way to say otherwise. */}
          <VarietalPicker purpose="move" /* Correcting a balance that already exists. (#358) */ label="Which honey?" hint="The correction applies to this varietal's balance."
            enabled={showVarietals}
            value={correctForm.varietal_id}
            onChange={(v) => setCorrectForm((f) => ({ ...f, varietal_id: v }))}
          />
          {/* Asks for the MEASURED total, not the difference. That is the
              number the user has in front of them; working out the drift is
              the app's job, not theirs. */}
          <Field
            label="What does the tank actually hold?"
            hint={(() => {
              // The figure quoted must be the one the server will
              // subtract from, or the difference it records is nonsense
              // (reported 25 Aug: 42 entered against a 56 lb tank
              // recorded General as -14).
              const forSelection = currentForSelection();
              if (forSelection !== null) {
                return `The app currently thinks this varietal holds ${String(forSelection)} lb.`;
              }
              if (showVarietals) return "Enter what this varietal holds, not the whole tank.";
              return poolPounds === null
                ? undefined
                : `The app currently thinks ${poolPounds.toFixed(2).replace(/\.00$/, "")} lb.`;
            })()}
          >
            {(id, aria) => <input id={id} {...aria} type="number" step="0.01" min="0" value={correctForm.pounds}
              onChange={(e) => setCorrectForm((f) => ({ ...f, pounds: e.target.value }))} className="touch-target" />}
          </Field>
          <Field label="Why (optional)" hint="Worth a word — it stays in the history.">
            {(id, aria) => <input id={id} {...aria} value={correctForm.reason}
              onChange={(e) => setCorrectForm((f) => ({ ...f, reason: e.target.value }))} className="touch-target" />}
          </Field>
        </div>
      </Modal>


    </div>
  );
}
